/** Village identity, fixed specializations and the seven-level campaign upgrade. */
import { provinceDef } from '../../data/provinces.ts';
import type { ResourceKey } from '../types.ts';
import type { CampaignState, HoldingKind, VillageType } from './types.ts';
export type { HoldingKind, VillageType } from './types.ts';
import { developBlock, developCost, garrisonCapAt, holdingIncome, supplyRouteOf,
  type DevelopCost, type HoldingIncome, type SupplyRoute } from './holdings.ts';

export const MAX_VILLAGE_LEVEL = 7;
export interface VillageProfile {
  id: VillageType;
  name: string;
  primaryResource: ResourceKey;
  description: string;
}
export const VILLAGE_PROFILES: readonly VillageProfile[] = [
  { id: 'farming', name: 'Farming village', primaryResource: 'grain', description: 'Fields, barns and farmsteads supply the nearest owned city.' },
  { id: 'woodcutting', name: 'Woodcutting village', primaryResource: 'timber', description: 'Woodlots and sawyers keep city builders supplied with timber.' },
  { id: 'stone_quarry', name: 'Quarry village', primaryResource: 'stone', description: 'Quarries cut stone for city works; a sturdy local watch guards the carts.' },
  { id: 'iron_mining', name: 'Iron-mining village', primaryResource: 'iron', description: 'Mine crews bring ore to the city forges and maintain a strong village watch.' },
  { id: 'fishing', name: 'Fishing village', primaryResource: 'fish', description: 'Fishing boats and smokehouses deliver food to the nearest owned city.' },
  { id: 'soul_shrine', name: 'Shrine village', primaryResource: 'souls', description: 'A fixed shrine settlement gathers souls for the crown.' },
  { id: 'gold_mining', name: 'Gold-mining village', primaryResource: 'gold', description: 'The village works a gold seam and pays its proceeds to the crown.' },
];

export function villageTypeForProvince(p: number): VillageType {
  const def = provinceDef(p);
  if (def.resource === 'souls') return 'soul_shrine';
  if (def.resource === 'gold') return 'gold_mining';
  if (def.resource === 'iron') return 'iron_mining';
  if (def.resource === 'stone') return 'stone_quarry';
  if (def.resource === 'timber') return 'woodcutting';
  if (def.terrain === 'coast') return 'fishing';
  if (def.terrain === 'forest' || def.terrain === 'jungle') return 'woodcutting';
  if (def.terrain === 'hill') return 'iron_mining';
  if (['mountain', 'desert', 'ice', 'ruin'].includes(def.terrain)) return 'stone_quarry';
  return 'farming';
}

/** Fallback is only for old in-memory fixtures; generation/save migration writes identity. */
export function holdingKind(c: CampaignState, p: number): HoldingKind {
  return c.provinces[p]?.holdingKind ?? (c.cities.some(city => city.at === p) || (c.provinces[p]?.tier ?? 1) >= 4 ? 'city' : 'village');
}
export function isVillageHolding(c: CampaignState, p: number): boolean {
  return !!c.provinces[p] && holdingKind(c, p) === 'village';
}
export function villageLevel(c: CampaignState, p: number): number {
  return Math.max(1, Math.min(MAX_VILLAGE_LEVEL, Math.round(c.provinces[p]?.tier ?? 1)));
}
export function villageTypeOf(c: CampaignState, p: number): VillageType {
  return c.provinces[p]?.villageType || villageTypeForProvince(p);
}
export function villageProfile(c: CampaignState, p: number): VillageProfile | null {
  if (!isVillageHolding(c, p)) return null;
  const type = villageTypeOf(c, p);
  return VILLAGE_PROFILES.find(profile => profile.id === type) ?? null;
}
export function villageLevelStats(type: VillageType, level: number): { yieldMultiplier: number; garrisonBonus: number; fort: number; port: number } {
  const l = Math.max(1, Math.min(MAX_VILLAGE_LEVEL, Math.round(level)));
  return {
    yieldMultiplier: [1, 1.2, 1.45, 1.75, 2.1, 2.55, 3.1][l - 1],
    garrisonBonus: 12 + (l - 1) * 8 + (type === 'stone_quarry' || type === 'iron_mining' ? 10 : type === 'soul_shrine' ? 6 : 0),
    fort: l >= 6 ? 2 : l >= 3 ? 1 : 0,
    port: type === 'fishing' ? (l >= 6 ? 2 : l >= 3 ? 1 : 0) : 0,
  };
}
export function villageIncome(c: CampaignState, p: number, atLevel?: number): HoldingIncome {
  return holdingIncome(c, p, atLevel);
}
export function nearestOwnedCity(c: CampaignState, p: number): SupplyRoute {
  return supplyRouteOf(c, p);
}
export function villageUpgradeCost(c: CampaignState, p: number): DevelopCost | null {
  return isVillageHolding(c, p) ? developCost(c, p) : null;
}
export function villageUpgradeBlock(c: CampaignState, p: number, nation: number): string {
  return isVillageHolding(c, p) ? developBlock(c, p, nation) : 'Only villages use this upgrade.';
}
export interface VillageReport {
  profile: VillageProfile;
  level: number;
  maxLevel: number;
  income: HoldingIncome;
  defense: { fort: number; garrison: number };
  route: SupplyRoute;
  upgrade: DevelopCost | null;
  block: string;
  worksRemaining: number;
  heldGoods: Array<{ key: ResourceKey; amount: number }>;
}
export function villageReport(c: CampaignState, p: number, nation = c.playerNation): VillageReport | null {
  const profile = villageProfile(c, p);
  if (!profile) return null;
  return { profile, level: villageLevel(c, p), maxLevel: MAX_VILLAGE_LEVEL,
    income: villageIncome(c, p), defense: { fort: c.provinces[p].fort, garrison: garrisonCapAt(c, p) },
    route: nearestOwnedCity(c, p), upgrade: villageUpgradeCost(c, p), block: villageUpgradeBlock(c, p, nation),
    worksRemaining: c.provinces[p].worksT,
    heldGoods: Object.entries(c.provinces[p].villageStock).map(([key, amount]) => ({ key: key as ResourceKey, amount: amount ?? 0 })).filter(g => g.amount > 0) };
}
